// ThemeContext.js
import React from 'react';
import { PiletApi } from '@sharefiledev/sharefile-appshell';

type ContextValue = {
	app?: PiletApi;
};

export const AppContext = React.createContext<ContextValue>({ app: undefined });

/**
 * Wraps a React functional component with the AppContext provider, to allow the component to access the PiletApi instance passed as argument in call to this function.
 * Usage:
 * ```tsx
 * import React from 'react';
 * import { AppContext } from '@utils/context';
 *
 * const MyComponent: React.FC = () => {
 * 	const { app } = React.useContext(AppContext);
 * 	return <button onClick={() => {
 * 		app?.showModal('modalname', someOptions)
 * 	}}>
 * 		Click to show a modal using PiletApi!
 * 	</button>;
 * 	;
 * @param app the PiletApi instance
 * @param Component the React functional component to wrap
 * @returns the wrapped component
 */
export function withAppContext<TProps extends Record<string, unknown>>(
	app: PiletApi,
	Component: React.ComponentType<TProps>
): React.FC<TProps> {
	const WrappedComponent: React.FC<TProps> = props => (
		<AppContext.Provider value={{ app }}>
			<Component {...props} />
		</AppContext.Provider>
	);
	return WrappedComponent;
}

export const useAppContext = () => React.useContext(AppContext);
